import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
  computed,
  OnInit,
  DestroyRef,
  HostListener,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { BehaviorSubject, combineLatest, map, filter } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  LucideAngularModule,
  ChevronLeft,
  ChevronRight,
  Map,
  FileText,
} from 'lucide-angular';
import { LessonPlanService } from '../../../../services/lesson-plan.service';
import {
  LessonSlide,
  GreetingSlideContent,
  TextSlideContent,
  ListSlideContent,
  ByeSlideContent,
  SlideThumbnail,
} from '../../../../services/lesson-plan.models';
import { LessonMapComponent } from '../../../../components/lesson-map/lesson-map';
import { SlideCanvasComponent } from './slide-canvas/slide-canvas';
import { FeedbackModalComponent } from '../../../../../../../common/components/feedback-modal/feedback-modal';

export type MobileTab = 'lesson-plan' | 'teacher-script';

@Component({
  selector: 'app-presenter-view',
  standalone: true,
  imports: [CommonModule, LucideAngularModule, LessonMapComponent, SlideCanvasComponent, FeedbackModalComponent],
  templateUrl: './presenter-view.html',
  styleUrl: './presenter-view.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PresenterViewComponent implements OnInit {
  protected readonly lessonPlanService = inject(LessonPlanService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly isFeedbackModalOpen = signal<boolean>(false);
  protected readonly isEditRequestModalOpen = signal<boolean>(false);

  // Mobile responsive state
  protected readonly isMobile = signal<boolean>(false);
  protected readonly isTablet = signal<boolean>(false);
  protected readonly mobileActiveTab = signal<MobileTab>('lesson-plan');

  protected readonly slides$ = this.lessonPlanService.slides$;
  protected readonly planStructure$ = this.lessonPlanService.planStructure$;
  protected readonly thumbnails$ = this.lessonPlanService.thumbnails$;
  protected readonly isFetchingThumbnails$ = this.lessonPlanService.isFetchingThumbnails$;
  protected readonly googleSlides$ = this.lessonPlanService.googleSlides$;

  protected readonly currentSlideIndex = signal<number>(0);

  protected readonly currentSlide$ = combineLatest([
    this.slides$,
    new BehaviorSubject(this.currentSlideIndex),
  ]).pipe(
    map(([slides]) => {
      const index = this.currentSlideIndex();
      return slides[index] || null;
    })
  );

  protected readonly slidesCount$ = this.slides$.pipe(map((slides) => slides.length));

  // Get the active block ID from the current slide's blockId property
  protected readonly activeBlockId = computed(() => {
    const slides = this.lessonPlanService.slides;
    const planStructure = this.lessonPlanService.planStructure;
    const index = this.currentSlideIndex();

    if (slides.length === 0 || planStructure.length === 0) return 1;

    const currentSlide = slides[index];
    if (currentSlide?.blockId) {
      return currentSlide.blockId;
    }

    // Fallback to first block
    return planStructure[0]?.id || 1;
  });

  protected readonly icons = {
    ChevronLeft,
    ChevronRight,
    Map,
    FileText,
  };

  @HostListener('window:resize')
  onResize(): void {
    this.checkScreenSize();
  }

  private checkScreenSize(): void {
    const width = window.innerWidth;
    this.isMobile.set(width < 768);
    this.isTablet.set(width >= 768 && width < 1024);
  }

  protected setMobileTab(tab: MobileTab): void {
    this.mobileActiveTab.set(tab);
  }

  ngOnInit(): void {
    this.checkScreenSize();
    // Fetch thumbnails when component initializes if we have a Google Slides presentation
    this.lessonPlanService.googleSlides$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(gs => !!gs?.presentationId)
      )
      .subscribe(() => {
        // Only fetch if we don't already have thumbnails
        if (this.lessonPlanService.thumbnails.length === 0) {
          this.lessonPlanService.fetchThumbnails().subscribe();
        }
      });
  }

  /**
   * Get thumbnail URL for the current slide index
   */
  protected getThumbnailForSlide(slideIndex: number): SlideThumbnail | null {
    const thumbnails = this.lessonPlanService.thumbnails;
    return thumbnails.find(t => t.slideIndex === slideIndex) || null;
  }

  protected nextSlide(): void {
    const slides = this.lessonPlanService.slides;
    if (this.currentSlideIndex() < slides.length - 1) {
      this.currentSlideIndex.update((i) => i + 1);
    }
  }

  protected prevSlide(): void {
    if (this.currentSlideIndex() > 0) {
      this.currentSlideIndex.update((i) => i - 1);
    }
  }

  protected getCurrentPhase(): string {
    const plan = this.lessonPlanService.planStructure;
    const blockId = this.activeBlockId();
    return plan.find((b) => b.id === blockId)?.phase || 'Presentation';
  }

  /**
   * Get speaker notes for a slide - uses AI-generated teacherScript if available,
   * otherwise falls back to generated notes based on slide content
   */
  protected getSlideNotes(slide: LessonSlide): { text: string; isAction: boolean }[] {
    const notes: { text: string; isAction: boolean }[] = [];

    // Use AI-generated teacher script if available
    if (slide.teacherScript) {
      notes.push({ text: slide.teacherScript, isAction: false });
      return notes;
    }

    // Fallback to generated notes based on slide type
    switch (slide.type) {
      case 'greeting': {
        const content = slide.content as GreetingSlideContent;
        notes.push({ text: `Welcome students and introduce today's topic: "${content.title}"`, isAction: false });
        notes.push({ text: `This lesson is designed for ${content.grade} students studying ${content.subject}.`, isAction: false });
        notes.push({ text: '[Action: Give students a moment to settle and focus their attention]', isAction: true });
        break;
      }
      case 'text': {
        const content = slide.content as TextSlideContent;
        notes.push({ text: `Present the concept: "${content.title}"`, isAction: false });
        notes.push({ text: content.subtitle, isAction: false });
        notes.push({ text: '[Action: Check for understanding - ask if students have any questions]', isAction: true });
        break;
      }
      case 'list': {
        const content = slide.content as ListSlideContent;
        notes.push({ text: `Explain "${content.title}" and discuss each point:`, isAction: false });
        if (content.subtitle) {
          notes.push({ text: content.subtitle, isAction: false });
        }
        const items = content.items.split('\n').filter(i => i.trim());
        items.forEach((item, i) => {
          notes.push({ text: `${i + 1}. ${item}`, isAction: false });
        });
        notes.push({ text: '[Action: Pause after each point to allow students to take notes]', isAction: true });
        break;
      }
      case 'bye': {
        const content = slide.content as ByeSlideContent;
        notes.push({ text: `Closing: ${content.text}`, isAction: false });
        notes.push({ text: 'Summarize key takeaways from today\'s lesson.', isAction: false });
        notes.push({ text: '[Action: Answer any final questions and remind students of homework if applicable]', isAction: true });
        break;
      }
    }

    return notes;
  }

  protected getSlideNumber(): number {
    return this.currentSlideIndex() + 1;
  }

  protected onSlideSelected(event: { slideIndex: number; blockId: number }): void {
    this.currentSlideIndex.set(event.slideIndex);
  }

  protected openFeedbackModal(): void {
    this.isFeedbackModalOpen.set(true);
  }

  protected closeFeedbackModal(): void {
    this.isFeedbackModalOpen.set(false);
  }

  protected openEditRequestModal(): void {
    this.isEditRequestModalOpen.set(true);
  }

  protected closeEditRequestModal(): void {
    this.isEditRequestModalOpen.set(false);
  }
}
